import React, { createContext, useContext, useState, useEffect } from "react";

const ThemeContext = createContext(null);

const PRESETS = {
  light: {
    bg: "#ffffff",
    highlight: "#6366f1",
    name: "Light",
    textPrimary: "#0f172a",
    textSecondary: "#475569",
    textMuted: "#94a3b8",
    bgCard: "rgba(0,0,0,0.03)",
    bgCardHover: "rgba(0,0,0,0.06)",
    bgInput: "rgba(0,0,0,0.04)",
    borderSubtle: "rgba(0,0,0,0.1)",
    borderHighlight: "rgba(0,0,0,0.15)",
    isDark: false,
  },
  dark: {
    bg: "#000000",
    highlight: "#6366f1",
    name: "Dark",
    textPrimary: "#f1f5f9",
    textSecondary: "#a1a1aa",
    textMuted: "#71717a",
    bgCard: "rgba(255,255,255,0.05)",
    bgCardHover: "rgba(255,255,255,0.08)",
    bgInput: "rgba(255,255,255,0.07)",
    borderSubtle: "rgba(255,255,255,0.1)",
    borderHighlight: "rgba(255,255,255,0.15)",
    isDark: true,
  },
  blue: {
    bg: "#0f172a",
    highlight: "#6366f1",
    name: "Blue",
    textPrimary: "#f1f5f9",
    textSecondary: "#94a3b8",
    textMuted: "#64748b",
    bgCard: "rgba(255,255,255,0.04)",
    bgCardHover: "rgba(255,255,255,0.07)",
    bgInput: "rgba(255,255,255,0.06)",
    borderSubtle: "rgba(255,255,255,0.08)",
    borderHighlight: "rgba(255,255,255,0.12)",
    isDark: true,
  },
  custom: {
    bg: "#0f172a",
    highlight: "#6366f1",
    name: "Custom",
    textPrimary: "#f1f5f9",
    textSecondary: "#94a3b8",
    textMuted: "#64748b",
    bgCard: "rgba(255,255,255,0.04)",
    bgCardHover: "rgba(255,255,255,0.07)",
    bgInput: "rgba(255,255,255,0.06)",
    borderSubtle: "rgba(255,255,255,0.08)",
    borderHighlight: "rgba(255,255,255,0.12)",
    isDark: true,
  },
};

export function ThemeProvider({ children }) {
  const [themeName, setThemeName] = useState(() =>
    localStorage.getItem("aiq_theme") || "light"
  );
  const [customBg, setCustomBg] = useState(() =>
    localStorage.getItem("aiq_custom_bg") || "#0f172a"
  );
  const [customHighlight, setCustomHighlight] = useState(() =>
    localStorage.getItem("aiq_custom_hl") || "#6366f1"
  );

  const currentTheme = themeName === "custom"
    ? {
        ...PRESETS.custom,
        bg: customBg,
        highlight: customHighlight,
      }
    : PRESETS[themeName] || PRESETS.light;

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--bg-primary", currentTheme.bg);
    root.style.setProperty("--color-highlight", currentTheme.highlight);
    root.style.setProperty("--color-highlight-glow", currentTheme.highlight + "40");
    root.style.setProperty("--color-highlight-soft", currentTheme.highlight + "20");
    root.style.setProperty("--text-primary", currentTheme.textPrimary);
    root.style.setProperty("--text-secondary", currentTheme.textSecondary);
    root.style.setProperty("--text-muted", currentTheme.textMuted);
    root.style.setProperty("--bg-card", currentTheme.bgCard);
    root.style.setProperty("--bg-card-hover", currentTheme.bgCardHover);
    root.style.setProperty("--bg-input", currentTheme.bgInput);
    root.style.setProperty("--border-subtle", currentTheme.borderSubtle);
    root.style.setProperty("--border-highlight", currentTheme.borderHighlight);

    localStorage.setItem("aiq_theme", themeName);
    localStorage.setItem("aiq_custom_bg", customBg);
    localStorage.setItem("aiq_custom_hl", customHighlight);
  }, [themeName, customBg, customHighlight, currentTheme]);

  return (
    <ThemeContext.Provider
      value={{
        themeName,
        setThemeName,
        customBg,
        setCustomBg,
        customHighlight,
        setCustomHighlight,
        currentTheme,
        presets: PRESETS,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
